
import { AppConfig } from './types';
import { defaultConfig } from './default';
import { developmentConfig } from './development';
import { stageConfig } from './stage';
import { productionConfig } from './production';
import { subEnvironmentConfig } from './subenvironment';
import { localConfig } from './local';

/**
 * Valid environment names for configuration
 */
export type Environment = 'production' | 'development' | 'staging' | 'subenvironment' | 'local';



/**
 * Deep merge utility function to combine configuration objects
 * This implementation maintains immutability and properly handles arrays
 */
function deepMerge<T extends Record<string, any>>(
  target: T,
  ...sources: Partial<T>[]
): T {
  // Create a deep clone of the target to maintain immutability
  const result: T = JSON.parse(JSON.stringify(target));
  
  if (!sources.length) return result;
  
  // Process each source object
  for (const source of sources) {
    if (!source) continue;
    
    for (const key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        const sourceValue = source[key];
        const resultValue = result[key];
        
        // Handle different types of values
        if (sourceValue === null || sourceValue === undefined) {
          continue; // Skip null/undefined values
        } else if (Array.isArray(sourceValue)) {
          // Handle arrays - create a new array with all elements
          result[key] = [...sourceValue] as any;
        } else if (
          typeof sourceValue === 'object' &&
          resultValue &&
          typeof resultValue === 'object' &&
          !Array.isArray(resultValue)
        ) {
          // For nested objects, recursively merge
          result[key] = deepMerge(
            resultValue as Record<string, any>,
            sourceValue as Record<string, any>
          ) as any;
        } else {
          // For primitive values, simply assign
          result[key] = sourceValue as T[Extract<keyof T, string>];
        }
      }
    }
  }
  
  return result;
}

/**
 * Determine which environment configuration to use based on provided environment
 * @param env The environment name
 */
function getEnvironmentConfig(env: Environment = 'local'): Partial<AppConfig> {
  switch (env) {
    case 'production':
      return productionConfig;
    case 'development':
      return developmentConfig;
    case 'staging':
      return stageConfig;
    case 'subenvironment':
      return subEnvironmentConfig;
    case 'local':
      return localConfig;
    default:
      return defaultConfig; // Default to development if unknown
  }
}

/**
 * Build the final configuration by merging default, environment, and sub-environment configs
 * @param env The environment name
 */
function buildConfig(env: Environment = 'development'): AppConfig {
  // Start with the default configuration
  const baseConfig = deepMerge({} as Partial<AppConfig>, defaultConfig);
  
  // Merge with environment-specific configuration
  const envConfig = getEnvironmentConfig(env);
  const mergedConfig = deepMerge(baseConfig, envConfig);
  
  return mergedConfig as AppConfig;
}

// Export the default configuration (using 'development' environment)
export const config: AppConfig = buildConfig();

/**
 * Create a configuration for a specific environment
 * @param env The environment name
 */
export function createConfig(env: Environment): AppConfig {
  return buildConfig(env);
}

/**
 * Helper function to get a typed configuration value
 * @param path Dot notation path to the configuration value
 * @param defaultValue Default value to return if the path doesn't exist
 * @param configObj Optional custom config object to use instead of the default config
 */
export function getConfigValue<T>(path: string, defaultValue?: T, configObj: AppConfig = config): T | undefined {
  const parts = path.split('.');
  let current: any = configObj;
  
  for (const part of parts) {
    if (current === undefined || current === null) {
      return defaultValue;
    }
    current = current[part];
  }
  
  return current !== undefined ? current : defaultValue;
}

/**
 * Extend the shared configuration with context-specific configuration
 * @param contextName The name of the context (e.g., 'vtc', 'session')
 * @param contextConfig The context-specific configuration to merge with the shared config
 * @param baseConfig Optional base config to extend (defaults to the shared config)
 * @returns A new configuration object with the merged values
 */
export function extendConfig<T extends Record<string, any>>(
  contextName: string, 
  contextConfig: Partial<AppConfig> & T, 
  baseConfig: AppConfig = config
): AppConfig & Record<string, any> {
  // Create a new object with the shared config as the base
  const wrappedConfig = { [contextName]: contextConfig };
  const mergedConfig = deepMerge({} as AppConfig & Record<string, any>, baseConfig, wrappedConfig);
  return mergedConfig as AppConfig & Record<string, any>;
}

